<template>
  <teleport to="#modals-container">
    <ShiftTemplateHandler
      :key="sidebarKey"
      :is-open="shiftHandlerIsOpen"
      :shift="shiftHandlerData"
      :users="users"
      @close-shift-handler="shiftHandlerToggle()"
      @add-new-shift-submit="newShiftSubmit"
      @update-shift-submit="updateShiftSubmit"
      @delete-shift="deleteShift"
    />
  </teleport>

  <PageHeader
    breadcrumb
    :items="[
      { label: $t('ui.entities.setting', 2), to: '/settings' },
      { label: $t('ui.entities.schedule.template.label') }
    ]"
  >
    <template #right>
      <div class="flex items-center gap-4">
        <Select
          v-model="callCenterSelected"
          :options="callCentersAllowed"
          icon="headset"
          value-key="id"
          label-key="name"
          :placeholder="`Select a Call Center`"
          class="min-w-56"
          :readonly="callCentersAllowed.length == 1"
          @select="updateCallCenterSelected"
        ></Select>
        <Dropdown :padding="false">
          <template #triggerContent>
            <Button
              label="Apply Template"
              is-dropdown
              outlined
            />
          </template>
          <div class="px-6 py-4">
            <FormGroup label="Apply template to">
              <DatePicker
                v-model="applyTemplateDateRange"
                display-format="Do MMM"
                selection-mode="week"
                :close-on-select="false"
              />
            </FormGroup>
            <Select
              v-model="callCenterSelected"
              :options="callCentersAllowed"
              icon="headset"
              value-key="id"
              label-key="name"
              :placeholder="`Select a Call Center`"
              class="max-w-[250px]"
              :readonly="callCentersAllowed.length == 1"
              @select="updateCallCenterSelected"
            ></Select>
            <div class="max-w-[240px] flex mb-4">
              <Icon
                type="info-circle"
                class="text-info mr-2"
              />
              <div class="text-secondary text-sm">
                Applying the template won't replace the shifts that are already live.
              </div>
            </div>
            <Button
              label="Apply"
              class="mb-2"
              block
              :is-loading="applyTemplateApiRequest.isLoading.value"
              @click="applyTemplate"
            />
          </div>
        </Dropdown>
      </div>
    </template>
  </PageHeader>

  <div>
    <Card
      :is-loading="getShiftsRequest.isLoading.value"
      class="min-h-[50vh]"
    >
      <div v-if="usersComputed?.length">
        <div
          class="schedule-header grid"
          :style="'grid-template-columns: repeat(' + (columns.length + 2) + ', minmax(0, 1fr));'"
        >
          <div class="schedule-header-cell col-span-2">
            <Input
              v-model="search"
              icon="search"
              placeholder="Search"
            />
          </div>
          <div
            v-for="column in columns"
            :key="column"
            class="schedule-header-cell text-center"
          >
            <div>{{ $t('ui.common.dateTime.dayOfWeek.' + column) }}</div>
            <Chip
              v-tippy="{
                content:
                  'Productive: ' + getDayHours(column).productive + ' <br> Shifts Total: ' + getDayHours(column).total
              }"
              size="sm"
              icon="clock"
            >
              <span>
                {{ round(getDayHours(column).productive, 1) || '0' }} / {{ round(getDayHours(column).total, 1) || '0' }}
              </span>
            </Chip>
          </div>
        </div>
        <div
          v-for="user in usersComputed"
          :key="user.id"
          class="schedule-row grid"
          :style="'grid-template-columns: repeat(' + (columns.length + 2) + ', minmax(0, 1fr));'"
        >
          <div class="schedule-row-side col-span-2 flex justify-between">
            <AvatarLabelGroup
              :label="user.fullName"
              :description="capitalize(user?.callCenterRole)"
              :avatar-url="user.profilePicUrl"
              :avatar-text="user.initials"
            >
              <template #description>
                <Chip
                  v-tippy="{
                    content:
                      'Productive: ' + getWeekHours(user).productive + ' <br> Shifts Total: ' + getWeekHours(user).total
                  }"
                  size="sm"
                  icon="clock"
                  class="mt-1"
                >
                  <span>{{ getWeekHours(user).productive }} / {{ getWeekHours(user).total }}</span>
                </Chip>
              </template>
            </AvatarLabelGroup>

            <ProgressCircle
              v-model="getWeekHours(user).differencePercentage"
              v-tippy="{
                content:
                  'Productive: ' + getWeekHours(user).productive + ' <br> Shifts Total: ' + getWeekHours(user).total
              }"
              :display-value="getWeekHours(user).displayValue"
              size="xs"
              :severity="
                getWeekHours(user).differencePercentage < 100
                  ? 'warning'
                  : getWeekHours(user).differencePercentage == 100
                    ? 'success'
                    : 'error'
              "
            >
              <template #display-value>
                <span
                  v-if="getWeekHours(user).differencePercentage != 100"
                  :class="getWeekHours(user).differencePercentage < 100 ? 'text-warning' : 'text-error'"
                >
                  {{ getWeekHours(user).displayValue }}
                </span>
                <Icon
                  v-else
                  type="check"
                  class="text-success"
                />
              </template>
            </ProgressCircle>
          </div>
          <div
            v-for="column in columns"
            :key="column"
            class="drop-block"
            @drop="onDrop($event, user.id, column)"
            @dragenter.prevent
            @dragover.prevent
            @dragenter="highlightDrop($event)"
            @dragleave="unhighlightDrop($event)"
            @click.self="addShift($event, user.id, column)"
          >
            <div v-if="!getShiftsRequest.isLoading.value && getShift(user.id, column)?.length > 0">
              <div
                v-for="shift in getShift(user.id, column)"
                :key="shift.id"
                class="shift-block"
                draggable="true"
                @dragstart="startDrag($event, shift)"
                @click="shiftHandlerToggle(shift)"
              >
                <div
                  class="shift-block-delete"
                  @click.stop="deleteShift(shift)"
                >
                  <Icon type="trash" />
                </div>
                <div class="font-medium text-[14px] flex justify-center items-center gap-1">
                  {{ formatShiftTime(shift.start) }}
                  <Icon
                    type="arrow-right"
                    class="text-secondary text-xs"
                  />
                  {{ formatShiftTime(shift.end) }}
                </div>
                <div class="text-[13px] text-slate-400 flex justify-center gap-4">
                  <span class="flex items-center">
                    <Icon
                      type="business-time"
                      class="text-[10px] mr-[2px]"
                    />
                    <span>{{ shift.productiveHours }}</span>
                  </span>
                  <span class="flex items-center">
                    <Icon
                      type="phone"
                      class="text-[10px] mr-[2px]"
                    />
                    <span>{{ shift.callTarget }}</span>
                  </span>
                  <span class="flex items-center">
                    <Icon
                      type="calendar"
                      class="text-[10px] mr-[2px]"
                    />
                    <span>{{ shift.appointmentTarget }}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        v-else-if="!callCenterSelected"
        class="py-32"
      >
        <Empty
          :title="'Select a call center'"
          icon="headset"
        ></Empty>
      </div>
      <div
        v-else-if="!callCentersAllowed?.length"
        class="py-32"
      >
        <Empty
          title="No call centers"
          description="You haven't been added to any call centers"
          icon="headset"
        ></Empty>
      </div>
      <div
        v-else-if="!usersComputed?.length"
        class="py-32"
      >
        <Empty
          :title="`No users found`"
          description="Add users to the call center to see their schedule"
          icon="users"
        >
          <template #buttons>
            <Button
              v-if="callCenterSelected"
              label="Add users"
              icon="plus"
              :router-link="`/settings/call-centers/${callCenterSelected}/users`"
            />
          </template>
        </Empty>
      </div>

      <div
        v-if="usersComputed?.length"
        class="grid"
        :style="'grid-template-columns: repeat(' + (columns.length + 2) + ', minmax(0, 1fr));'"
      >
        <div class="p-5 col-span-2 flex justify-between">
          <span>{{ $t('ui.common.total') }}</span>
          <Chip
            v-tippy="{
              content:
                'Productive: ' +
                getWeekHoursTotal(user).productive +
                ' <br> Shifts Total: ' +
                getWeekHoursTotal(user).total
            }"
            size="sm"
            icon="clock"
            class="mt-1"
          >
            <span>{{ getWeekHoursTotal(user).productive }} / {{ getWeekHoursTotal(user).total }}</span>
          </Chip>
        </div>
      </div>
    </Card>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { capitalize } from 'lodash'

import ShiftTemplateHandler from '@/components/unique/ShiftTemplateHandler.vue'
import PageHeader from '@/components/page/PageHeader.vue'
import Card from '@/components/card/Card.vue'
import Icon from '@/components/icon/Icon.vue'
import Empty from '@/components/empty/Empty.vue'
import Select from '@/components/forms/Select.vue'
import Chip from '@/components/chip/Chip.vue'
import ProgressCircle from '@/components/progress/ProgressCircle.vue'
import AvatarLabelGroup from '@/components/avatar/AvatarLabelGroup.vue'
import Button from '@/components/button/Button.vue'
import Dropdown from '@/components/dropdown/Dropdown.vue'
import DatePicker from '@/components/forms/DatePicker.vue'
import FormGroup from '@/components/forms/FormGroup.vue'
import Input from '@/components/forms/Input.vue'

import { alertToast } from '@/utilities/notification'
import useApiRequest from '@/composables/useApiRequest'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import Swal from 'sweetalert2'

dayjs.extend(customParseFormat)
import { round } from 'lodash'

// stores
import { useCallCenterStore } from '@/stores/CallCenterStore.ts'

const callCenterStore = useCallCenterStore()

const shifts = ref(null)
const users = ref(null)
const search = ref('')

const callCentersAllowed = callCenterStore.callCenters
const callCenterSelected = ref(parseInt(localStorage.getItem('report-filter-call-center-selected')) || null)

const applyTemplateDateRange = ref([
  dayjs().startOf('week').format('YYYY-MM-DD'),
  dayjs().endOf('week').format('YYYY-MM-DD')
])

const shiftHandlerIsOpen = ref(false)
const shiftHandlerData = ref()

const getShiftsRequest = useApiRequest()
const getUsersRequest = useApiRequest()
const deleteShiftRequest = useApiRequest()
const copyShiftRequest = useApiRequest()
const moveShiftRequest = useApiRequest()

function updateCallCenterSelected() {
  localStorage.setItem('report-filter-call-center-selected', callCenterSelected.value)
  fetchUsers()
  fetchShifts()
}

const { t } = useI18n()
const sidebarKey = ref(Math.random())

function fetchUsers() {
  if (callCenterSelected.value == null) {
    return
  }
  try {
    getUsersRequest
      .send({
        method: 'GET',
        endpoint: '/v1/users',
        params: {
          userType: 'all',
          callCenterId: callCenterSelected.value
        }
      })
      .then(response => {
        if (response.success) {
          if (response.data.length == 0) {
            users.value = []
          } else {
            users.value = response.data
            // Order users by name
            users.value?.sort((a, b) => {
              return a.fullName.localeCompare(b.fullName)
            })
          }
        } else {
          alertToast('Failed to fetch users', response.message, 'error')
        }
      })
  } catch (err) {
    alertToast('Failed to fetch users', err.message, 'error')
  }
}
fetchUsers()

function fetchShifts() {
  try {
    if (callCenterSelected.value == null) {
      return
    }
    getShiftsRequest
      .send({
        method: 'GET',
        endpoint: '/v1/users/schedule/template',
        params: {
          callCenterId: callCenterSelected.value
        }
      })
      .then(response => {
        if (response.success) {
          shifts.value =
            response.data?.map(shift => ({
              ...shift,
              start: `2025-01-01 ${shift.start}`,
              end: `2025-01-01 ${shift.end}`
            })) || []
        } else {
          alertToast('Failed to fetch shifts', response.message, 'error')
        }
      })
  } catch (err) {
    alertToast('Failed to fetch shifts', err.message, 'error')
  }
}
fetchShifts()

const columns = ref(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'])

const getShift = (userId, column) => {
  const filteredShifts = shifts.value?.filter(shift => shift.userId == userId && shift.dayOfWeek == column)

  // Sort the filtered shifts by start time
  filteredShifts?.sort((a, b) => {
    return a.start.localeCompare(b.start)
  })

  return filteredShifts
}

const getWeekHours = user => {
  const userShifts = shifts.value?.filter(shift => shift.userId == user.id)
  const totalHours = userShifts?.reduce((n, { totalShiftHours }) => n + totalShiftHours, 0)
  const productiveHours = userShifts?.reduce((n, { productiveHours }) => n + productiveHours, 0)

  if (totalHours > 0) {
    return {
      total: totalHours,
      productive: productiveHours,
      differencePercentage: (productiveHours / totalHours) * 100,
      displayValue: productiveHours - totalHours
    }
  } else {
    return {
      total: 0,
      productive: 0,
      differencePercentage: 0,
      displayValue: ''
    }
  }
}

const getWeekHoursTotal = () => {
  const totalHours = shifts.value?.reduce((n, { totalShiftHours }) => n + totalShiftHours, 0)
  const productiveHours = shifts.value?.reduce((n, { productiveHours }) => n + productiveHours, 0)

  if (totalHours > 0) {
    return {
      total: totalHours,
      productive: productiveHours,
      differencePercentage: (productiveHours / totalHours) * 100,
      displayValue: productiveHours - totalHours
    }
  } else {
    return {
      total: 0,
      productive: 0,
      differencePercentage: 0,
      displayValue: ''
    }
  }
}

const getDayHours = column => {
  // get all the shifts from all users for the day
  const dayShifts = shifts.value?.filter(shift => shift.dayOfWeek == column)
  const totalHours = dayShifts?.reduce((n, { totalShiftHours }) => n + totalShiftHours, 0)
  const productiveHours = dayShifts?.reduce((n, { productiveHours }) => n + productiveHours, 0)

  return {
    total: totalHours,
    productive: productiveHours
  }
}

const startDrag = (event, shift) => {
  if (event.ctrlKey) {
    // Copy
    event.dataTransfer.dropEffect = 'copy'
    event.dataTransfer.effectAllowed = 'copy'
  } else {
    // Move
    event.dataTransfer.dropEffect = 'move'
    event.dataTransfer.effectAllowed = 'move'
  }
  event.dataTransfer.setData('shiftID', shift.id)
}

function copyShift(event, shiftData) {
  // Show spinner
  event.target.classList.add('drop-block-spinner')

  try {
    copyShiftRequest
      .send({ method: 'POST', endpoint: '/v1/users/schedule/template', data: shiftData })
      .then(response => {
        if (response.data != '' && response.success) {
          // Add shift to shifts array
          shifts.value.push(response.data)
          alertToast('Copied', null, 'success')

          // Hide spinner
          event.target.classList.remove('drop-block-spinner')
        } else {
          alertToast('Failed to copy the shift', response.message, 'error')
          throw Error('Error posting data')
        }
      })
  } catch (err) {
    alertToast('Failed to copy the shift', err.message, 'error')
  }
}

function moveShift(data, userId, column) {
  const updatedData = { ...data }
  updatedData.userId = userId
  updatedData.dayOfWeek = column

  try {
    moveShiftRequest
      .send({
        method: 'PATCH',
        endpoint: '/v1/users/schedule/template/' + updatedData.id,
        data: updatedData
      })
      .then(response => {
        if (response.data && response.success) {
          data.dayOfWeek = column
          data.userId = userId

          alertToast('Updated', response.message, 'success')
        } else {
          alertToast('Failed to update', response.message, 'error')
          throw Error('Error patching data')
        }
      })
  } catch (err) {
    alertToast('Failed to update', err.message, 'error')
  }
}

const highlightDrop = event => {
  // console.log(event.target)
  event.target.classList.add('dragover')
}

const unhighlightDrop = event => {
  event.target.classList.remove('dragover')
}

const onDrop = (event, userId, column) => {
  const shiftID = event.dataTransfer.getData('shiftID')
  const shift = shifts.value.find(shift => shift.id == shiftID)
  if (event.shiftKey) {
    // Copy shift
    const newShift = { ...shift }
    newShift.userId = userId
    newShift.dayOfWeek = column

    // Create new shift in DB
    copyShift(event, newShift)
  } else {
    // Move shift
    moveShift(shift, userId, column)
  }
  event.target.classList.remove('dragover')
}
const formatShiftTime = time => {
  if (!time) return ''
  // Try parsing as full datetime first
  let parsed = dayjs(time, 'YYYY-MM-DD HH:mm:ss', true)
  if (!parsed.isValid()) {
    // Try parsing as time-only format
    parsed = dayjs(time, ['HH:mm:ss', 'HH:mm', 'hh:mm:ss', 'hh:mm'], true)
    if (parsed.isValid()) {
      // Use today's date with the parsed time for formatting
      parsed = dayjs().hour(parsed.hour()).minute(parsed.minute()).second(parsed.second() || 0)
    }
  }
  return parsed.isValid() ? parsed.format('HH:mm') : ''
}

const addShift = (event, userId, dayOfWeek) => {
  // console.log(event.target)
  shiftHandlerData.value = { userId: userId, dayOfWeek: dayOfWeek }
  shiftHandlerToggle()
}

function shiftHandlerToggle(shift) {
  sidebarKey.value = Math.random() // Change key so sidebar will re-mount
  shiftHandlerIsOpen.value = !shiftHandlerIsOpen.value

  if (shift) {
    // If update shift
    shiftHandlerData.value = shift
  }
}

function newShiftSubmit(data) {
  shifts.value.push(data)
  shiftHandlerToggle()
}

function updateShiftSubmit(data) {
  // Update shift with new data
  const shiftIndex = shifts.value.findIndex(x => x.id === data.id)
  shifts.value[shiftIndex] = data

  shiftHandlerToggle()
}

async function deleteShift(shift) {
  if (!shift.id) {
    return
  }
  Swal.fire({
    title: 'Are you sure?',
    text: 'You will not be able to recover this shift!',
    icon: 'warning',
    reverseButtons: true,
    showCloseButton: true,
    showCancelButton: true,
    confirmButtonText: '<i class="fa fa-trash"></i> ' + t('ui.crud.delete'),
    confirmButtonColor: 'var(--bg-error-solid)',
    cancelButtonText: t('ui.actions.cancel'),
    cancelButtonColor: 'var(--bg-tertiary)'
  }).then(function (response) {
    if (response.isConfirmed) {
      try {
        deleteShiftRequest
          .send({ method: 'DELETE', endpoint: '/v1/users/schedule/template/' + shift.id })
          .then(response => {
            if (response.data != '' && response.success) {
              alertToast('Deleted', null, 'success')

              const shiftIndex = shifts.value.findIndex(x => x.id === shift.id)
              shifts.value.splice(shiftIndex, 1)
              shiftHandlerIsOpen.value = false
            } else {
              alertToast('Failed to delete shift', response.message, 'error')
              throw Error('Error deleting data')
            }
          })
      } catch (err) {
        alertToast('Failed to delete shift', err.message, 'error')
      }
    } else {
      shiftHandlerToggle()
    }
  })
}
const applyTemplateApiRequest = useApiRequest()

function applyTemplate() {
  try {
    applyTemplateApiRequest
      .send({
        method: 'POST',
        endpoint: '/v1/users/schedule/template/apply',
        data: {
          userIds: users.value.map(user => user.id),
          startDate: applyTemplateDateRange.value[0],
          endDate: applyTemplateDateRange.value[1]
        }
      })
      .then(response => {
        if (response.success) {
          alertToast('Template applied', null, 'success')
        } else {
          alertToast('Failed to apply template', response.message, 'error')
        }
      })
  } catch (err) {
    alertToast('Failed to apply template', err.message, 'error')
  }
}

const usersComputed = computed(() => {
  return users.value?.filter(u => !search.value || u.fullName.toLowerCase().includes(search.value.toLowerCase()))
})
</script>
